import { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams, useSearchParams } from "react-router-dom";
import AccountLayout from "./components/account/AccountLayout";
import AccountDashboardPage from "./pages/account/AccountDashboardPage";
import SavedSearchesPage from "./pages/account/SavedSearchesPage";
import FavoritesPage from "./pages/account/FavoritesPage";
import ProfilePage from "./pages/account/ProfilePage";
import SecurityPage from "./pages/account/SecurityPage";
import PreferencesPage from "./pages/account/PreferencesPage";
import NotificationsPage from "./pages/account/NotificationsPage";
import PartnerLayout from "./components/partner/PartnerLayout";
import PartnerAuthGate from "./components/partner/PartnerAuthGate";
import { ClientViewPartnerPortalRouteGroup } from "./components/partner/ClientViewPartnerPortalRoutes";
import PartnerDashboardPage from "./pages/partner/PartnerDashboardPage";
import { LegacyClientAdminPortalRoutes, CasaMapaAdminPortalRoutes, MidpointMediaAdminPortalRoutes } from "./components/clientPortal/ClientAdminPortalRoutes";
import { AutoMapaAdminPortalRouteGroup } from "./components/automapa/admin/AutoMapaAdminPortalRoutes";
import AdminClientPublishingPendingPage from "./pages/admin/clientPublishing/AdminClientPublishingPendingPage";
import AdminClientPublishingReviewPage from "./pages/admin/clientPublishing/AdminClientPublishingReviewPage";
import AdminClientOrganizationsPage from "./pages/admin/clientManagement/AdminClientOrganizationsPage";
import AdminClientOrganizationFormPage from "./pages/admin/clientManagement/AdminClientOrganizationFormPage";
import AdminClientOrganizationHubPage from "./pages/admin/clientManagement/AdminClientOrganizationHubPage";
import AdminClientUsersPage from "./pages/admin/clientManagement/AdminClientUsersPage";
import { AuthProvider } from "./auth/AuthProvider";
import { IntlProvider } from "./intl/IntlProvider.jsx";
import AuthDebugPanel from "./components/auth/AuthDebugPanel";
import MainApp from "./MainApp";
import PropertyDetailPage from "./pages/PropertyDetailPage";
import LoginPage from "./pages/LoginPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import UnauthorizedPage from "./pages/UnauthorizedPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SetPasswordPage from "./pages/SetPasswordPage";
import LegalDocumentPage from "./pages/legal/LegalDocumentPage";
import ConsentRequiredPage from "./pages/account/ConsentRequiredPage";
import AccountConsentsPage from "./pages/account/AccountConsentsPage";
import RequireAuth from "./auth/RequireAuth";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import AdminDealerLayout from "./pages/admin/dealers/AdminDealerLayout";
import {
  AdminDealerOverviewPage,
  AdminDealerContactsPage,
  AdminDealerLocationsPage,
  AdminDealerUsersPage,
  AdminDealerVerificationPage,
  AdminDealerTrialPage,
  AdminDealerNotesPage,
  AdminDealerActivityPage,
  AdminDealerLeadPreferencesPage,
} from "./pages/admin/dealers/dealerDetailPages";
import AdminDealerProspectsListPage, { AdminDealerProspectDetailPage } from "./pages/admin/dealers/AdminDealerProspectsPage";
import AdminDealerTrialsPage from "./pages/admin/dealers/AdminDealerTrialsPage";
import AdminDealersListPage from "./pages/admin/dealers/AdminDealersListPage";
import AdminDealerFormPage from "./pages/admin/dealers/AdminDealerFormPage";
import AdminDealerVerificationsPage from "./pages/admin/dealers/AdminDealerVerificationsPage";
import DealerLoginPage from "./pages/dealer/DealerLoginPage";
import DealerPendingPlaceholderPage from "./pages/dealer/DealerPendingPlaceholderPage";
import { DealerAuthProvider } from "./auth/DealerAuthProvider";
import { automapDealerPortalPath } from "./constants/automapDealerPortalRoutes";
import DealerPortalLayout from "./components/dealer/DealerPortalLayout";
import DealerInventoryListPage from "./pages/dealer/DealerInventoryListPage";
import DealerInventoryDemandPage from "./pages/dealer/DealerInventoryDemandPage";
import DealerInventoryFormPage from "./pages/dealer/DealerInventoryFormPage";
import DealerInventoryDetailPage from "./pages/dealer/DealerInventoryDetailPage";
import DealerInventoryQualityPage from "./pages/dealer/DealerInventoryQualityPage";
import DealerInventoryImportPage from "./pages/dealer/DealerInventoryImportPage";
import DealerInventoryImportDetailPage from "./pages/dealer/DealerInventoryImportDetailPage";
import DealerInventoryImportHistoryPage from "./pages/dealer/DealerInventoryImportHistoryPage";
import AdminListingsListPage from "./pages/admin/listings/AdminListingsListPage";
import AdminListingDetailPage from "./pages/admin/listings/AdminListingDetailPage";
import AdminListingEditPage from "./pages/admin/listings/AdminListingEditPage";
import AdminListingQueuePage from "./pages/admin/listings/AdminListingQueuePage";
import AdminInventoryQualityDashboardPage from "./pages/admin/inventory-quality/AdminInventoryQualityDashboardPage";
import AdminInventoryQualityQueuePage from "./pages/admin/inventory-quality/AdminInventoryQualityQueuePage";
import AdminInventoryQualityRulesPage from "./pages/admin/inventory-quality/AdminInventoryQualityRulesPage";
import AdminTrustSafetyDashboardPage from "./pages/admin/trust-safety/AdminTrustSafetyDashboardPage";
import AdminTrustSafetyCasesPage from "./pages/admin/trust-safety/AdminTrustSafetyCasesPage";
import AdminTrustSafetyCaseDetailPage from "./pages/admin/trust-safety/AdminTrustSafetyCaseDetailPage";
import AdminTrustSafetyReportsPage from "./pages/admin/trust-safety/AdminTrustSafetyReportsPage";
import {
  AdminTrustSafetyDealersPage,
  AdminTrustSafetyListingsPage,
  AdminTrustSafetyRulesPage,
} from "./pages/admin/trust-safety/AdminTrustSafetyQueuePages";
import ReportListingPage from "./pages/public/ReportListingPage";
import ReportDealerPage from "./pages/public/ReportDealerPage";
import SafetyGuidePage from "./pages/public/SafetyGuidePage";
import BuyerChecklistHubPage from "./pages/public/BuyerChecklistHubPage";
import SellYourCarPage from "./pages/public/SellYourCarPage";
import PublicVehicleDetailPage from "./pages/public/PublicVehicleDetailPage";
import AutomapGlobalSeo from "./seo/AutomapGlobalSeo";
import AppPageHead from "./seo/AppPageHead";
import SeoLandingPage from "./pages/public/SeoLandingPage";
import ConcesionariosLegacySlugRoute from "./pages/public/ConcesionariosLegacySlugRoute";
import SeoGuidePage from "./pages/public/SeoGuidePage";
import BuyerChecklistGuidePage from "./pages/public/BuyerChecklistGuidePage";
import MyChecklistPage from "./pages/public/MyChecklistPage";
import AdminInventoryImportsListPage from "./pages/admin/inventory/AdminInventoryImportsListPage";
import AdminInventoryImportNewPage from "./pages/admin/inventory/AdminInventoryImportNewPage";
import AdminInventoryImportDetailPage from "./pages/admin/inventory/AdminInventoryImportDetailPage";
import DealerDashboardPage from "./pages/dealer/DealerDashboardPage";
import DealerLeadsListPage from "./pages/dealer/DealerLeadsListPage";
import DealerLeadDetailPage from "./pages/dealer/DealerLeadDetailPage";
import AdminLeadsListPage from "./pages/admin/leads/AdminLeadsListPage";
import AdminLeadDetailPage from "./pages/admin/leads/AdminLeadDetailPage";
import AdminSellerSubmissionsListPage from "./pages/admin/seller-submissions/AdminSellerSubmissionsListPage";
import AdminSellerSubmissionDetailPage from "./pages/admin/seller-submissions/AdminSellerSubmissionDetailPage";
import AdminSellerIntakeMonitoringPage from "./pages/admin/seller-submissions/AdminSellerIntakeMonitoringPage";
import AdminAttributionPage from "./pages/admin/analytics/AdminAttributionPage";
import AdminBuyerAnalyticsPage from "./pages/admin/analytics/AdminBuyerAnalyticsPage";
import AdminFunnelAnalyticsPage from "./pages/admin/analytics/AdminFunnelAnalyticsPage";
import AdminSearchAnalyticsPage from "./pages/admin/analytics/AdminSearchAnalyticsPage";
import AdminQuizAnalyticsPage from "./pages/admin/analytics/AdminQuizAnalyticsPage";
import AdminLeadAnalyticsPage from "./pages/admin/analytics/AdminLeadAnalyticsPage";
import AdminDealerAnalyticsPage from "./pages/admin/analytics/AdminDealerAnalyticsPage";
import AdminMarketplaceAnalyticsPage from "./pages/admin/analytics/marketplace/AdminMarketplaceHubPage";
import AdminSupplyAnalyticsPage from "./pages/admin/analytics/marketplace/AdminSupplyAnalyticsPage";
import AdminDemandAnalyticsPage from "./pages/admin/analytics/marketplace/AdminDemandAnalyticsPage";
import AdminCityAnalyticsPage from "./pages/admin/analytics/marketplace/AdminCityAnalyticsPage";
import AdminPricingAnalyticsPage from "./pages/admin/analytics/marketplace/AdminPricingAnalyticsPage";
import AdminInventoryHealthAnalyticsPage from "./pages/admin/analytics/marketplace/AdminInventoryHealthAnalyticsPage";
import AdminTrustSafetyAnalyticsPage from "./pages/admin/analytics/marketplace/AdminTrustSafetyAnalyticsPage";
import AdminSeoAnalyticsPage from "./pages/admin/analytics/marketplace/AdminSeoAnalyticsPage";
import AdminCampaignAnalyticsPage from "./pages/admin/analytics/marketplace/AdminCampaignAnalyticsPage";
import AdminCampaignsHubPage from "./pages/admin/campaigns/AdminCampaignsHubPage";
import AdminLaunchDashboardPage from "./pages/admin/campaigns/AdminLaunchDashboardPage";
import AdminLaunchReadinessPage from "./pages/admin/campaigns/AdminLaunchReadinessPage";
import AdminLaunchDailyMonitorPage from "./pages/admin/campaigns/AdminLaunchDailyMonitorPage";
import AdminLaunchAssetsPage from "./pages/admin/campaigns/AdminLaunchAssetsPage";
import AdminLaunchLeadsPage from "./pages/admin/campaigns/AdminLaunchLeadsPage";
import AdminPlaybookHubPage from "./pages/admin/playbook/AdminPlaybookHubPage";
import AdminPlaybookSectionPage from "./pages/admin/playbook/AdminPlaybookSectionPage";
import AdminPlaybookTemplatesPage from "./pages/admin/playbook/AdminPlaybookTemplatesPage";
import AdminPlaybookChecklistsPage from "./pages/admin/playbook/AdminPlaybookChecklistsPage";
import LaunchLandingPage from "./pages/public/LaunchLandingPage";
import AdminDealerPerformancePage from "./pages/admin/dealers/AdminDealerPerformancePage";
import AdminFoundingDashboardPage from "./pages/admin/dealers/founding/AdminFoundingDashboardPage";
import AdminFoundingPipelinePage from "./pages/admin/dealers/founding/AdminFoundingPipelinePage";
import AdminFoundingReadinessPage from "./pages/admin/dealers/founding/AdminFoundingReadinessPage";
import AdminFoundingImportsPage from "./pages/admin/dealers/founding/AdminFoundingImportsPage";
import AdminFoundingTasksPage from "./pages/admin/dealers/founding/AdminFoundingTasksPage";
import AdminDealerOnboardingPage from "./pages/admin/dealers/AdminDealerOnboardingPage";
import AdminPlansDashboardPage from "./pages/admin/plans/AdminPlansDashboardPage";
import AdminPlanDealersPage from "./pages/admin/plans/AdminPlanDealersPage";
import AdminPlanFeaturesPage from "./pages/admin/plans/AdminPlanFeaturesPage";
import AdminDealerPlanDetailPage from "./pages/admin/plans/AdminDealerPlanDetailPage";
import AdminPlacementsDashboardPage from "./pages/admin/placements/AdminPlacementsDashboardPage";
import AdminListingPlacementsPage from "./pages/admin/placements/AdminListingPlacementsPage";
import AdminDealerPlacementsPage from "./pages/admin/placements/AdminDealerPlacementsPage";
import AdminPlacementSchedulePage from "./pages/admin/placements/AdminPlacementSchedulePage";
import AdminPlacementPerformancePage from "./pages/admin/placements/AdminPlacementPerformancePage";
import AdminPlacementRulesPage from "./pages/admin/placements/AdminPlacementRulesPage";
import AdminExpansionDashboardPage from "./pages/admin/expansion/AdminExpansionDashboardPage";
import AdminExpansionCitiesPage from "./pages/admin/expansion/AdminExpansionCitiesPage";
import AdminExpansionCityFormPage from "./pages/admin/expansion/AdminExpansionCityFormPage";
import {
  AdminExpansionCityDetailPage,
  AdminExpansionCityReadinessPage,
  AdminExpansionCityDealersPage,
  AdminExpansionCityInventoryPage,
  AdminExpansionCitySeoPage,
  AdminExpansionCityCampaignsPage,
  AdminExpansionCityPlacementsPage,
  AdminExpansionCityAnalyticsPage,
} from "./pages/admin/expansion/AdminExpansionCityPages";
import AdminPartnersListPage from "./pages/admin/partners/AdminPartnersListPage";
import AdminPartnerFormPage from "./pages/admin/partners/AdminPartnerFormPage";
import AdminPartnerDetailPage from "./pages/admin/partners/AdminPartnerDetailPage";
import AdminPartnerLeadsPage from "./pages/admin/partners/AdminPartnerLeadsPage";
import AdminPartnerLeadDetailPage from "./pages/admin/partners/AdminPartnerLeadDetailPage";
import AdminPartnerPerformancePage from "./pages/admin/partners/AdminPartnerPerformancePage";
import {
  AdminPartnerCategoriesPage,
  AdminPartnerRoutingPage,
  AdminPartnerDisclosuresPage,
} from "./pages/admin/partners/AdminPartnerConfigPages";
import AdminPartnerAnalyticsPage from "./pages/admin/analytics/AdminPartnerAnalyticsPage";
import ServicesHubPage from "./pages/public/ServicesHubPage";
import ServiceCategoryPage from "./pages/public/ServiceCategoryPage";
import DealerPlanPage from "./pages/dealer/DealerPlanPage";
import DealerSponsorshipsPage from "./pages/dealer/DealerSponsorshipsPage";
import DealerOnboardingPage from "./pages/dealer/DealerOnboardingPage";
import DealerReportsHubPage from "./pages/dealer/reports/DealerReportsHubPage";
import DealerListingPerformancePage from "./pages/dealer/reports/DealerListingPerformancePage";
import DealerLeadPerformanceReportPage from "./pages/dealer/reports/DealerLeadPerformanceReportPage";
import DealerInventoryPerformancePage from "./pages/dealer/reports/DealerInventoryPerformancePage";
import DealerSourceReportPage from "./pages/dealer/reports/DealerSourceReportPage";
import CampaignAttributionTracker from "./components/automap/attribution/CampaignAttributionTracker";
import BuyerAnalyticsTracker from "./components/automap/analytics/BuyerAnalyticsTracker";
import ClientViewDetailPage from "./pages/admin/ClientViewDetailPage";
import TransportReportsListPage from "./pages/reports/TransportReportsListPage";
import TransportReportDetailPage from "./pages/reports/TransportReportDetailPage";
import TransportNeighborhoodMobilityPage from "./pages/transport/public/TransportNeighborhoodMobilityPage";
import TransportMetroStationPage from "./pages/transport/public/TransportMetroStationPage";
import TransportAccessPage from "./pages/transport/public/TransportAccessPage";
import TransportCommunityReportsPublicPage from "./pages/transport/public/TransportCommunityReportsPublicPage";
import TransportClientViewLandingPage from "./pages/transport/public/TransportClientViewLandingPage";
import AutomotivePathRedirect from "./components/midpoint/transport/AutomotivePathRedirect";
import ClientViewPublicUsersPage from "./pages/admin/ClientViewPublicUsersPage";
import ClientViewLaunchPage from "./pages/admin/ClientViewLaunchPage";
import AdminNotificationEventsPage from "./pages/admin/AdminNotificationEventsPage";
import WeatherSourceHealthPage from "./pages/admin/WeatherSourceHealthPage";
import WeatherSchedulerPage from "./pages/admin/WeatherSchedulerPage";
import WeatherCommercialAdminPage from "./pages/admin/WeatherCommercialAdminPage";
import WeatherSourceTransparencyPage from "./pages/weather/public/WeatherSourceTransparencyPage";
import {
  NotificationUnsubscribePage,
  NotificationPreferencesTokenPage,
  NotificationDisableSavedSearchPage,
  NotificationDisableListingWatchPage,
} from "./pages/notifications/NotificationTokenPage";
import ClientViewPreviewPage from "./pages/clientViews/ClientViewPreviewPage";
import ClientViewPublicRedirect from "./pages/clientViews/ClientViewPublicRedirect";
import { buildClientViewStudioPath } from "./utils/clientViewsRoutes";
import ScrollToTop from "./components/routing/ScrollToTop";

const HostBasedClientViewRouter = lazy(
  () => import("./components/clientViews/HostBasedClientViewRouter")
);
const ClientViewRuntimePage = lazy(
  () => import("./pages/clientViews/ClientViewRuntimePage")
);

const hostRouterFallback = (
  <div className="midpoint-admin-page" style={{ padding: "2rem" }}>
    <p>Loading…</p>
  </div>
);

function AdminClientViewStudioRedirect() {
  const { clientViewId } = useParams();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  return <Navigate to={buildClientViewStudioPath(clientViewId, tab || undefined)} replace />;
}

function DealerLegacyRedirect() {
  const location = useLocation();
  const suffix = location.pathname.replace(/^\/dealer\/?/, "");
  const target = suffix
    ? `${automapDealerPortalPath(suffix)}${location.search}${location.hash}`
    : automapDealerPortalPath("dashboard");
  return <Navigate to={target} replace />;
}

const AppRoot = () => (
  <BrowserRouter>
    <ScrollToTop />
    <AuthProvider>
      <IntlProvider surface="platform">
      <CampaignAttributionTracker />
      <BuyerAnalyticsTracker />
      <Suspense fallback={hostRouterFallback}>
        <HostBasedClientViewRouter>
          <AppPageHead />
          <AutomapGlobalSeo />
          <Routes>
        <Route path="/notifications/unsubscribe/:token" element={<NotificationUnsubscribePage />} />
        <Route
          path="/notifications/preferences/:token"
          element={<NotificationPreferencesTokenPage />}
        />
        <Route
          path="/notifications/saved-search/:token/disable"
          element={<NotificationDisableSavedSearchPage />}
        />
        <Route
          path="/notifications/listing-watch/:token/disable"
          element={<NotificationDisableListingWatchPage />}
        />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/terms" element={<LegalDocumentPage />} />
        <Route path="/privacy" element={<LegalDocumentPage />} />
        <Route path="/cookies" element={<LegalDocumentPage />} />
        <Route path="/email-notifications-policy" element={<LegalDocumentPage />} />
        <Route path="/broker-partner-terms" element={<LegalDocumentPage />} />
        <Route path="/seguridad" element={<SafetyGuidePage variant="seguridad" />} />
        <Route path="/guias/compra-segura" element={<SafetyGuidePage variant="compra-segura" />} />
        <Route path="/lanzamiento" element={<LaunchLandingPage />} />
        <Route path="/comprar/carros-usados" element={<Navigate to="/v/automap/vehicles" replace />} />
        <Route path="/carros/:vehicleListingId" element={<PublicVehicleDetailPage />} />
        <Route path="/checklist-compra" element={<BuyerChecklistHubPage />} />
        <Route path="/vender-mi-carro" element={<SellYourCarPage />} />
        <Route path="/servicios" element={<ServicesHubPage />} />
        <Route path="/servicios/:serviceSlug" element={<ServiceCategoryPage />} />
        <Route path="/guias/checklist-compra-carro-usado" element={<BuyerChecklistGuidePage />} />
        <Route path="/mi-checklist" element={<MyChecklistPage />} />
        <Route path="/carros-usados-:citySlug" element={<SeoLandingPage />} />
        <Route path="/camionetas-usadas-:citySlug" element={<SeoLandingPage />} />
        <Route path="/carros-financiados-:citySlug" element={<SeoLandingPage />} />
        <Route path="/carros-automaticos-:citySlug" element={<SeoLandingPage />} />
        <Route path="/carros-economicos-:citySlug" element={<SeoLandingPage />} />
        <Route path="/automoviles-usados-:citySlug" element={<SeoLandingPage />} />
        <Route path="/pickups-usadas-:citySlug" element={<SeoLandingPage />} />
        <Route path="/carros-familiares-:citySlug" element={<SeoLandingPage />} />
        <Route path="/marcas/:makeSlug/:citySlug" element={<SeoLandingPage />} />
        <Route path="/marcas/:makeSlug" element={<SeoLandingPage />} />
        <Route path="/modelos/:makeSlug/:modelSlug/:citySlug" element={<SeoLandingPage />} />
        <Route path="/modelos/:makeSlug/:modelSlug" element={<SeoLandingPage />} />
        <Route path="/tipo/:bodyTypeSlug/:citySlug" element={<SeoLandingPage />} />
        <Route path="/tipo/:bodyTypeSlug" element={<SeoLandingPage />} />
        <Route path="/presupuesto/:budgetSlug/:citySlug" element={<SeoLandingPage />} />
        <Route path="/presupuesto/:budgetSlug" element={<SeoLandingPage />} />
        <Route path="/concesionarios" element={<Navigate to="/v/automap/concesionarios" replace />} />
        <Route path="/concesionarios/:dealerSlug" element={<ConcesionariosLegacySlugRoute />} />
        <Route path="/guias/:guideSlug" element={<SeoGuidePage />} />
        <Route path="/reportar/listing/:listingId" element={<ReportListingPage />} />
        <Route path="/reportar/concesionario/:dealerId" element={<ReportDealerPage />} />
        <Route
          path="/account/consent-required"
          element={
            <RequireAuth>
              <div className="midpoint-app auth-page account-area-app">
                <ConsentRequiredPage />
              </div>
            </RequireAuth>
          }
        />
        <Route element={<PartnerAuthGate />}>
          {AutoMapaAdminPortalRouteGroup()}
          {ClientViewPartnerPortalRouteGroup()}
        </Route>
        <Route path="/partner/legacy" element={<PartnerLayout />}>
          <Route path="dashboard" element={<PartnerDashboardPage />} />
        </Route>
        {LegacyClientAdminPortalRoutes()}
        {CasaMapaAdminPortalRoutes()}
        {MidpointMediaAdminPortalRoutes()}
        <Route path="/account" element={<AccountLayout />}>
          <Route index element={<Navigate to="/account/dashboard" replace />} />
          <Route path="dashboard" element={<AccountDashboardPage />} />
          <Route path="saved-searches" element={<SavedSearchesPage />} />
          <Route path="favorites" element={<FavoritesPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="preferences" element={<PreferencesPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="consents" element={<AccountConsentsPage />} />
        </Route>
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route path="/" element={<MainApp />} />
        <Route path="/crm" element={<MainApp />} />
        <Route path="/crm/:section" element={<MainApp />} />
        <Route path="/crm/:section/:entityId" element={<MainApp />} />
        <Route path="/transport" element={<MainApp />} />
        <Route path="/transport/neighborhoods/:slug" element={<TransportNeighborhoodMobilityPage />} />
        <Route path="/transport/metro/:stationSlug" element={<TransportMetroStationPage />} />
        <Route path="/transport/access/:areaSlug" element={<TransportAccessPage />} />
        <Route path="/transport/community-reports" element={<TransportCommunityReportsPublicPage />} />
        <Route path="/weather/sources" element={<WeatherSourceTransparencyPage />} />
        <Route path="/weather/transparency" element={<Navigate to="/weather/sources" replace />} />
        <Route path="/transport/vehicles/analytics/classes/:classKey" element={<MainApp />} />
        <Route path="/transport/vehicles/analytics" element={<MainApp />} />
        <Route path="/transport/vehicles/deals" element={<MainApp />} />
        <Route path="/transport/vehicles/depreciation" element={<MainApp />} />
        <Route path="/transport/vehicles/alerts" element={<MainApp />} />
        <Route path="/transport/vehicles/saved-searches" element={<MainApp />} />
        <Route path="/transport/vehicles/:vehicleListingId" element={<MainApp />} />
        <Route path="/transport/:section" element={<MainApp />} />
        <Route
          path="/client/:clientViewKey/transport"
          element={<TransportClientViewLandingPage />}
        />
        <Route
          path="/reports/transport"
          element={
            <RequireAuth>
              <div className="midpoint-app">
                <TransportReportsListPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/reports/transport/:reportKey"
          element={
            <RequireAuth>
              <div className="midpoint-app">
                <TransportReportDetailPage />
              </div>
            </RequireAuth>
          }
        />
        <Route path="/community" element={<MainApp />} />
        <Route path="/community/:section" element={<MainApp />} />
        <Route path="/food" element={<MainApp />} />
        <Route path="/food/:section" element={<MainApp />} />
        <Route path="/culture" element={<MainApp />} />
        <Route path="/culture/:section" element={<MainApp />} />
        <Route path="/automotive" element={<AutomotivePathRedirect />} />
        <Route path="/automotive/:section" element={<AutomotivePathRedirect />} />
        <Route path="/gov" element={<MainApp />} />
        <Route path="/gov/:section" element={<MainApp />} />
        <Route path="/client-views" element={<MainApp />} />
        <Route path="/client-views/:clientViewId/studio" element={<MainApp />} />
        <Route
          path="/admin/client-views/:clientViewId"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <ClientViewDetailPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/client-views/launch"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <ClientViewLaunchPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/client-views/:clientViewId/studio"
          element={
            <RequireAuth>
              <AdminClientViewStudioRedirect />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/client-views/:clientViewId/public-users"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <ClientViewPublicUsersPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/notifications/events"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminNotificationEventsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/client-publishing/pending"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminClientPublishingPendingPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/client-publishing/:contentVersionId"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminClientPublishingReviewPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/client-organizations"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminClientOrganizationsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/client-organizations/new"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminClientOrganizationFormPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/client-organizations/:clientOrgId/edit"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminClientOrganizationFormPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/client-organizations/:clientOrgId/users"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminClientOrganizationHubPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/client-organizations/:clientOrgId/views"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminClientOrganizationHubPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/client-organizations/:clientOrgId/audit-log"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminClientOrganizationHubPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/client-organizations/:clientOrgId"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminClientOrganizationHubPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/client-users"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminClientUsersPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/weather/sources"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <WeatherSourceHealthPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/weather/scheduler"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <WeatherSchedulerPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/weather/commercial"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <WeatherCommercialAdminPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireAuth>
              <Navigate to="/admin/dashboard" replace />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dashboard"
          element={
            <RequireAuth>
              <AdminDashboardPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers/founding/pipeline"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminFoundingPipelinePage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers/founding/readiness"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminFoundingReadinessPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers/founding/imports"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminFoundingImportsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers/founding/tasks"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminFoundingTasksPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers/founding/onboarding"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminFoundingDashboardPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers/founding"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminFoundingDashboardPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminDealersListPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers/new"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminDealerFormPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers/prospects"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminDealerProspectsListPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers/prospects/:prospectId"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminDealerProspectDetailPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers/verifications"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminDealerVerificationsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers/trials/needs-attention"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminDealerTrialsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers/trials/ready-to-convert"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminDealerTrialsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers/trials/ending-soon"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminDealerTrialsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers/trials/active"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminDealerTrialsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/plans/dealers"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminPlanDealersPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/plans/features"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminPlanFeaturesPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/plans"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminPlansDashboardPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/placements/listings"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminListingPlacementsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/placements/dealers"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminDealerPlacementsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/placements/schedule"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminPlacementSchedulePage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/placements/performance"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminPlacementPerformancePage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/placements/rules"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminPlacementRulesPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/placements"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminPlacementsDashboardPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/expansion/cities/new"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminExpansionCityFormPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/expansion/cities/:citySlug/readiness"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminExpansionCityReadinessPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/expansion/cities/:citySlug/dealers"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminExpansionCityDealersPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/expansion/cities/:citySlug/inventory"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminExpansionCityInventoryPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/expansion/cities/:citySlug/seo"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminExpansionCitySeoPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/expansion/cities/:citySlug/campaigns"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminExpansionCityCampaignsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/expansion/cities/:citySlug/placements"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminExpansionCityPlacementsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/expansion/cities/:citySlug/analytics"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminExpansionCityAnalyticsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/expansion/cities/:citySlug"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminExpansionCityDetailPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/expansion/cities"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminExpansionCitiesPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/expansion"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminExpansionDashboardPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/partners/new"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminPartnerFormPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/partners/categories"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminPartnerCategoriesPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/partners/routing"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminPartnerRoutingPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/partners/disclosures"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminPartnerDisclosuresPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/partners/leads/:leadId"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminPartnerLeadDetailPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/partners/:partnerId/leads"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminPartnerLeadsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/partners/:partnerId/performance"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminPartnerPerformancePage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/partners/:partnerId/edit"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminPartnerFormPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/partners/:partnerId"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminPartnerDetailPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/partners"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminPartnersListPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/analytics/partners"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminPartnerAnalyticsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers/trials"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminDealerTrialsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers/:dealerId/edit"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminDealerFormPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers/:dealerId"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminDealerLayout />
              </div>
            </RequireAuth>
          }
        >
          <Route index element={<AdminDealerOverviewPage />} />
          <Route path="contacts" element={<AdminDealerContactsPage />} />
          <Route path="locations" element={<AdminDealerLocationsPage />} />
          <Route path="users" element={<AdminDealerUsersPage />} />
          <Route path="verification" element={<AdminDealerVerificationPage />} />
          <Route path="trial" element={<AdminDealerTrialPage />} />
          <Route path="notes" element={<AdminDealerNotesPage />} />
          <Route path="activity" element={<AdminDealerActivityPage />} />
          <Route path="lead-preferences" element={<AdminDealerLeadPreferencesPage />} />
        </Route>
        <Route
          path="/admin/dealers/:dealerId/onboarding"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminDealerOnboardingPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers/:dealerId/readiness"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminDealerOnboardingPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers/:dealerId/plan"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminDealerPlanDetailPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/dealers/:dealerId/performance"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminDealerPerformancePage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/v/automap/dealer/login"
          element={
            <DealerAuthProvider>
              <DealerLoginPage />
            </DealerAuthProvider>
          }
        />
        <Route path="/v/automap/dealer/pending" element={<DealerPendingPlaceholderPage />} />
        <Route path="/v/automap/dealer" element={<Navigate to="/v/automap/dealer/dashboard" replace />} />
        <Route path="/v/automap/dealer/onboarding" element={<DealerPortalLayout />}>
          <Route index element={<DealerOnboardingPage />} />
        </Route>
        <Route path="/v/automap/dealer/dashboard" element={<DealerPortalLayout />}>
          <Route index element={<DealerDashboardPage />} />
        </Route>
        <Route path="/v/automap/dealer/plan" element={<DealerPortalLayout />}>
          <Route index element={<DealerPlanPage />} />
        </Route>
        <Route path="/v/automap/dealer/patrocinios" element={<DealerPortalLayout />}>
          <Route index element={<DealerSponsorshipsPage />} />
        </Route>
        <Route path="/v/automap/dealer/reportes" element={<DealerPortalLayout />}>
          <Route index element={<DealerReportsHubPage />} />
          <Route path="rendimiento" element={<DealerListingPerformancePage />} />
          <Route path="leads" element={<DealerLeadPerformanceReportPage />} />
          <Route path="inventario" element={<DealerInventoryPerformancePage />} />
          <Route path="fuentes" element={<DealerSourceReportPage />} />
        </Route>
        <Route path="/v/automap/dealer/leads" element={<DealerPortalLayout />}>
          <Route index element={<DealerLeadsListPage />} />
          <Route path="hot" element={<DealerLeadsListPage preset="hot" />} />
          <Route path="archivados" element={<DealerLeadsListPage preset="archived" />} />
          <Route path=":leadId" element={<DealerLeadDetailPage />} />
        </Route>
        <Route path="/v/automap/dealer/inventario" element={<DealerPortalLayout />}>
          <Route index element={<DealerInventoryListPage />} />
          <Route path="demanda" element={<DealerInventoryDemandPage />} />
          <Route path="importar" element={<DealerInventoryImportPage />} />
          <Route path="importaciones" element={<DealerInventoryImportHistoryPage />} />
          <Route path="importar/:importId" element={<DealerInventoryImportDetailPage />} />
          <Route path="nuevo" element={<DealerInventoryFormPage />} />
          <Route path="calidad" element={<DealerInventoryQualityPage />} />
          <Route path=":listingId/editar" element={<DealerInventoryFormPage />} />
          <Route path=":listingId" element={<DealerInventoryDetailPage />} />
        </Route>
        <Route path="/dealer/*" element={<DealerLegacyRedirect />} />
        <Route
          path="/admin/analytics/buyers"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminBuyerAnalyticsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/analytics/funnels"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminFunnelAnalyticsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/analytics/search"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminSearchAnalyticsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/analytics/quiz"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminQuizAnalyticsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/analytics/leads"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminLeadAnalyticsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/analytics/marketplace"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminMarketplaceAnalyticsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/analytics/supply"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminSupplyAnalyticsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/analytics/demand"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminDemandAnalyticsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/analytics/cities"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminCityAnalyticsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/analytics/pricing"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminPricingAnalyticsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/analytics/inventory-health"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminInventoryHealthAnalyticsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/analytics/trust-safety"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminTrustSafetyAnalyticsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/analytics/seo"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminSeoAnalyticsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/campaigns/launch/readiness"
          element={
            <RequireAuth>
              <AdminLaunchReadinessPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/campaigns/launch/daily-monitor"
          element={
            <RequireAuth>
              <AdminLaunchDailyMonitorPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/campaigns/launch/assets"
          element={
            <RequireAuth>
              <AdminLaunchAssetsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/campaigns/launch/leads"
          element={
            <RequireAuth>
              <AdminLaunchLeadsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/campaigns/launch"
          element={
            <RequireAuth>
              <AdminLaunchDashboardPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/playbook/checklists"
          element={
            <RequireAuth>
              <AdminPlaybookChecklistsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/playbook/templates"
          element={
            <RequireAuth>
              <AdminPlaybookTemplatesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/playbook/dealer-onboarding"
          element={
            <RequireAuth>
              <AdminPlaybookSectionPage slug="dealer-onboarding" />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/playbook/inventory-quality"
          element={
            <RequireAuth>
              <AdminPlaybookSectionPage slug="inventory-quality" />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/playbook/lead-followup"
          element={
            <RequireAuth>
              <AdminPlaybookSectionPage slug="lead-followup" />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/playbook/trust-safety"
          element={
            <RequireAuth>
              <AdminPlaybookSectionPage slug="trust-safety" />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/playbook/buyer-launch"
          element={
            <RequireAuth>
              <AdminPlaybookSectionPage slug="buyer-launch" />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/playbook/seo"
          element={
            <RequireAuth>
              <AdminPlaybookSectionPage slug="seo" />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/playbook/analytics"
          element={
            <RequireAuth>
              <AdminPlaybookSectionPage slug="analytics" />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/playbook/dealer-trials"
          element={
            <RequireAuth>
              <AdminPlaybookSectionPage slug="dealer-trials" />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/playbook/:sectionSlug"
          element={
            <RequireAuth>
              <AdminPlaybookSectionPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/playbook"
          element={
            <RequireAuth>
              <AdminPlaybookHubPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/campaigns"
          element={
            <RequireAuth>
              <AdminCampaignsHubPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/analytics/campaigns"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminCampaignAnalyticsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/analytics/dealers"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminDealerAnalyticsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/analytics/attribution"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminAttributionPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/leads"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminLeadsListPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/leads/:leadId"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminLeadDetailPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/seller-submissions/monitoring"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminSellerIntakeMonitoringPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/seller-submissions"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminSellerSubmissionsListPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/seller-submissions/:submissionId"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminSellerSubmissionDetailPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/inventory/imports"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminInventoryImportsListPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/inventory/imports/new"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminInventoryImportNewPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/inventory/imports/:importId"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminInventoryImportDetailPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/inventory-quality/rules"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminInventoryQualityRulesPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/inventory-quality/stale"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminInventoryQualityQueuePage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/inventory-quality/incomplete"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminInventoryQualityQueuePage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/inventory-quality/duplicates"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminInventoryQualityQueuePage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/inventory-quality/outliers"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminInventoryQualityQueuePage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/inventory-quality/dealer-followups"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminInventoryQualityQueuePage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/inventory-quality/queue"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminInventoryQualityQueuePage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/inventory-quality"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminInventoryQualityDashboardPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/trust-safety/rules"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminTrustSafetyRulesPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/trust-safety/listings"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminTrustSafetyListingsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/trust-safety/dealers"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminTrustSafetyDealersPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/trust-safety/reports"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminTrustSafetyReportsPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/trust-safety/cases/:caseId"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminTrustSafetyCaseDetailPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/trust-safety/cases"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminTrustSafetyCasesPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/trust-safety"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminTrustSafetyDashboardPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/listings/review"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminListingQueuePage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/listings/stale"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminListingQueuePage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/listings/missing-photos"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminListingQueuePage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/listings/duplicates"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminListingQueuePage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/listings/outliers"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminListingQueuePage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/listings"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminListingsListPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/listings/:listingId/edit"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminListingEditPage />
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/listings/:listingId"
          element={
            <RequireAuth>
              <div className="midpoint-app account-area-app">
                <AdminListingDetailPage />
              </div>
            </RequireAuth>
          }
        />
        <Route path="/client-views/:slug/preview" element={<ClientViewPreviewPage />} />
        <Route path="/client-views/:slug/*" element={<ClientViewPublicRedirect />} />
        <Route path="/client-views/:slug" element={<ClientViewPublicRedirect />} />
        <Route path="/v/:slug/*" element={<ClientViewRuntimePage />} />
        <Route path="/real-estate/properties/:propertyId" element={<PropertyDetailPage />} />
        <Route path="/properties/:propertyId" element={<PropertyDetailPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </HostBasedClientViewRouter>
      </Suspense>
      <AuthDebugPanel />
      </IntlProvider>
    </AuthProvider>
  </BrowserRouter>
);

export default AppRoot;
